import React, { useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import HeadData from 'components/Common/HeadData'
import Heading from 'LuxKit/Typography/Heading'
import ImageHero from 'components/Common/ImageHero'
import Group from 'components/utils/Group'
import Search from 'home/components/Search/Search'
import GeoContext from 'contexts/geoContext'
import { getHomeOfferListParams } from 'home/helpers'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'

const HeroContainer = styled(ImageHero)`
  max-height: ${rem(400)};
`

function Error404PageWithOffers() {
  const intl = useIntl()
  const { currentRegionCode, geoStateName } = useContext(GeoContext)

  const filters = useMemo(() => getHomeOfferListParams({
    region: currentRegionCode,
    subregion: geoStateName,
  }), [currentRegionCode, geoStateName])

  const pageTitle = intl.formatMessage({
    id: '8lH0KxwYRo-dnXxIEwLy',
    defaultMessage: 'Page Not Found',
    description: 'Page title for the 404 error page with offers shown in browser tab',
  })

  const pageDescription = intl.formatMessage({
    id: '-Q8xcZJ48yR28UfrDxHW',
    defaultMessage: '404 - page not found',
    description: 'Meta description for the 404 error page with offers',
  })

  const imageAlt = intl.formatMessage({
    id: '-EpkFEnbeaIKrsHOxSTA',
    defaultMessage: 'Oops! We can’t find that page',
    description: 'Alt text for hero image on 404 error page with offers',
  })

  const mainHeading = intl.formatMessage({
    id: 'vrcjeXgF7HaM8V3L-4bm',
    defaultMessage: 'Oops! We can’t find that page.',
    description: 'Main heading on 404 error page with offers on the offer page',
  })

  const subHeading = intl.formatMessage({
    id: '8fvP0a6Cky6Xbk6viRYY',
    defaultMessage: 'Let’s get you back on your journey.',
    description: 'Subheading on 404 error page encouraging user to continue browsing',
  })

  return (
    <div className="Error404Page T-Error404Page">
      <HeadData
        title={pageTitle}
        description={pageDescription}
      />
      <HeroContainer
        alt={imageAlt}
        aria-hidden="true"
        brightness={-40}
        gravity="auto"
        fit="center"
        quality="good"
        mobileAspectRatio="4:3"
        desktopAspectRatio="3:1"
        largeDesktopAspectRatio="4:1"
        id="hc71fr4ynk0ljlqnw399"
      >
        <Group data-testid="404-error-page" direction="vertical" horizontalAlign="center" gap={8}>
          <Heading variant="heading1" align="center" colour="neutral-eight">{mainHeading}</Heading>
          <Heading variant="heading4" align="center" colour="neutral-eight">{subHeading}</Heading>
        </Group>
      </HeroContainer>
      <Search />
      <OfferCardCarouselSection
        title={<FormattedMessage
          id="ea6wxjp5Sa59qnl35Uxz"
          defaultMessage="Explore our <i>top offers</i>"
          description="Title for top offers carousel section on 404 error page"
          values={{
            i: (chunks: React.ReactNode) => <i>{chunks}</i>,
          }}
        />}
        filters={filters}
      />
    </div>
  )
}

export default React.memo(Error404PageWithOffers)
